// src/components/registered-landing/tutor/TutorLandingPage.js
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import Navbar from "../../Navbar";
import Footer from "../../footer/Footer";

import styles from "./styles/TutorLandingPage.module.css";
import StatsOverviewCard from "./StatsOverviewCard";

export default function TutorLandingPage({ user, firstName }) {
  const router = useRouter();

  // ----------------------------
  // Unread count
  // ----------------------------
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadUnreadCount = async () => {
      try {
        const res = await fetch("/api/unread-count", {
          cache: "no-store",
          credentials: "include",
        });

        if (!res.ok) {
          if (!cancelled) setTotalUnread(0);
          return;
        }

        const data = await res.json();
        if (!cancelled) setTotalUnread(data?.totalUnread ?? 0);
      } catch {
        if (!cancelled) setTotalUnread(0);
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 20000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const unreadCount = totalUnread ?? 0;
  const hasUnread = unreadCount > 0;
  const displayUnread = hasUnread ? String(unreadCount) : "0";

  // ----------------------------
  // Tutor stats (safe defaults)
  // ----------------------------
  const tutorStats = useMemo(
    () => ({
      upcomingClasses: user?.tutorStats?.upcomingClasses ?? 0,
      enrolledLearners: user?.tutorStats?.enrolledLearners ?? 0,
      monthlyEarnings: user?.tutorStats?.monthlyEarnings ?? 0,
      courseViews: Array.isArray(user?.tutorStats?.courseViews)
        ? user.tutorStats.courseViews
        : [],
      estimatedFutureEarnings: user?.tutorStats?.estimatedFutureEarnings ?? 0,
      hoursTaught: user?.tutorStats?.hoursTaught ?? 0,
      learnersTaught: user?.tutorStats?.learnersTaught ?? 0,
      lifetimeEarnings: user?.tutorStats?.lifetimeEarnings ?? 0,
      monthlySummary: Array.isArray(user?.tutorStats?.monthlySummary)
        ? user.tutorStats.monthlySummary
        : [],
    }),
    [user]
  );

  const now = new Date();
  const fallbackLabel = now.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });

  const effectiveMonthlySummary =
    tutorStats.monthlySummary.length > 0
      ? tutorStats.monthlySummary
      : [
          {
            id: "sample-0",
            label: fallbackLabel,
            earnings: 0,
            learners: 0,
            hours: 0,
          },
        ];

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const openSummaryModal = () => setShowSummaryModal(true);
  const closeSummaryModal = () => setShowSummaryModal(false);

  const handleCreateClass = () => router.push("/create-course");
  const handleViewTutorClasses = () => router.push("/tutor/classes");
  const handleGoToTutorProfile = () => router.push("/settings");
  const handleOpenMessages = () => router.push("/chats");

  const formattedLifetimeEarnings = tutorStats.lifetimeEarnings
    ? `$${tutorStats.lifetimeEarnings.toLocaleString()}`
    : "$0";

  const formatMonthLabel = (entry) => {
    if (!entry) return "—";

    const { label, month, year } = entry;

    if (label) return label;
    if (month && year) return `${month} ${year}`;
    if (month) return month;

    return "—";
  };

  return (
    <>
      <Navbar />

      <main className={styles.container}>
        <section className={styles.tutorHero}>
          <div className={styles.tutorHeroCopy}>
            <p className={styles.tutorHeroEyebrow}>Tutor dashboard</p>

            <h1 className={styles.tutorHeroTitle}>
              Welcome back, {firstName}!
            </h1>

            <p className={styles.tutorHeroText}>
              Manage your classes, see who&apos;s enrolled, and keep track of your
              earnings — all in one place. We&apos;re here to help you grow as an
              educator.
            </p>

            <div className={styles.tutorSummaryRow}>
              <div className={styles.statsOverviewWrap}>
                <StatsOverviewCard
                  enrolledLearners={tutorStats.enrolledLearners}
                  hoursTaught={tutorStats.hoursTaught}
                  monthlyEarnings={tutorStats.monthlyEarnings}
                  courseViews={tutorStats.courseViews}
                  onOpenSummary={openSummaryModal}
                />
              </div>

              <section
                className={styles.tutorPanelCard}
                aria-label={
                  hasUnread
                    ? "Messages from families. You have unread questions."
                    : "Messages from families. No unread questions."
                }
              >
                <div className={styles.tutorPanelHeaderRow}>
                  <h3 className={styles.tutorPanelHeading}>
                    Messages from families
                  </h3>

                  {hasUnread && (
                    <span className={styles.unreadBadge} aria-label="New messages">
                      New
                    </span>
                  )}
                </div>

                <p className={styles.tutorPanelBody}>
                  Responding quickly builds trust and helps families feel
                  confident enrolling with you.
                </p>

                <div className={styles.tutorStatRow}>
                  <span className={styles.tutorStatLabel}>Unread questions</span>

                  <span
                    className={
                      hasUnread
                        ? `${styles.tutorStatValue} ${styles.tutorStatValueUnread}`
                        : styles.tutorStatValue
                    }
                  >
                    {displayUnread}
                  </span>
                </div>

                <button
                  type="button"
                  className={styles.tutorSmallButton}
                  onClick={handleOpenMessages}
                >
                  {hasUnread ? "View unread messages" : "Go to messages"}
                </button>
              </section>
            </div>
          </div>

          <aside className={styles.tutorHeroPanel}>
            <h2 className={styles.tutorPanelTitle}>Quick actions</h2>
            <p className={styles.tutorPanelText}>
              Start with your next class idea, or fine-tune your profile so
              families can get to know you.
            </p>

            <div className={styles.tutorHeroPanelButtons}>
              <button
                type="button"
                className={styles.tutorPrimaryButton}
                onClick={handleCreateClass}
              >
                Create a new Course
              </button>
              <button
                type="button"
                className={styles.tutorGhostButton}
                onClick={handleViewTutorClasses}
              >
                View my Courses
              </button>
              <button
                type="button"
                className={styles.tutorGhostButton}
                onClick={handleGoToTutorProfile}
              >
                Edit tutor profile
              </button>
            </div>
          </aside>
        </section>
      </main>

      {showSummaryModal && (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          onClick={closeSummaryModal}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Total Summary</h2>
              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={closeSummaryModal}
                aria-label="Close summary"
              >
                ×
              </button>
            </div>

            <div className={styles.summaryStatGrid}>
              <div className={styles.summaryStatCard}>
                <p className={styles.summaryStatLabel}>Learners taught</p>
                <p className={styles.summaryStatValue}>
                  {tutorStats.learnersTaught
                    ? tutorStats.learnersTaught.toLocaleString()
                    : 0}
                </p>
              </div>

              <div className={styles.summaryStatCard}>
                <p className={styles.summaryStatLabel}>Hours taught</p>
                <p className={styles.summaryStatValue}>
                  {tutorStats.hoursTaught
                    ? tutorStats.hoursTaught.toLocaleString()
                    : 0}{" "}
                  hrs
                </p>
              </div>

              <div className={styles.summaryStatCard}>
                <p className={styles.summaryStatLabel}>Lifetime earnings</p>
                <p className={styles.summaryStatValue}>
                  {formattedLifetimeEarnings}
                </p>
              </div>
            </div>

            <div className={styles.monthlySummarySection}>
              <h3 className={styles.monthlySummaryTitle}>Monthly Summary</h3>

              <div className={styles.summaryTableWrapper}>
                <table className={styles.summaryTable}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Earnings</th>
                      <th>Learners</th>
                      <th>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveMonthlySummary.map((entry, idx) => (
                      <tr key={entry.id || `${entry.month}-${entry.year}-${idx}`}>
                        <td>{formatMonthLabel(entry)}</td>
                        <td>
                          {entry.earnings
                            ? `$${entry.earnings.toLocaleString()}`
                            : "$0"}
                        </td>
                        <td>{entry.learners ?? 0}</td>
                        <td>{entry.hours ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {tutorStats.monthlySummary.length === 0 && (
                <p className={styles.monthlySummaryEmpty}>
                  Your monthly stats will update here as you teach more classes.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}